import { FlexLayout, Typography } from '@eduact/ed-system'
import Spacer from '@eduact/ed-components/Spacer'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text as FeedbackTitle } from '@eduact/ed-components/TestSummary/OrderingAnswer/OrderingAnswer'
import { FeedbacktWrapper } from '@eduact/ed-components/TestSummary/GapAnswer/GapAnswer'
import stampAutoDir from '@Utilities/StampAutoDir'
import { QuestionContentWrapper } from './QuestionStyled'
import { StoredAnswerEntry, entryMatchesOption } from './matching'

type Props = {
	question: any
	test: any
	status: string
}

/**
 * Vendored from ed-components 0.4.10 TestSummary/McqAnswer. Same rendering and
 * reveal gating; the stored-answer join moved from text equality to the
 * id-first rule, and a stored answer matching NO live option renders as an
 * orphan row instead of the question looking unanswered — see VENDORED.md.
 */
const McqAnswer: React.FC<Props> = ({ question, test, status }) => {
	const { t } = useTranslation(['test'])
	// Non-array guard: a question type-flipped from mrq leaves ARRAY-shaped
	// content behind; treating it as an mcq entry would fabricate an orphan
	// row with an undefined answer text.
	const rawStored = question?.answer?.content?.options
	const stored = (rawStored && !Array.isArray(rawStored) ? rawStored : null) as StoredAnswerEntry | null

	const content = useMemo(() => stampAutoDir(question.content), [question.content])
	const showCorrectAnswer = useMemo(() => status === 'passed' && test?.show_correct_if_passed === true, [status, test])
	const isStudentFailedRightAnswer = useMemo(
		() => status === 'failed' && test?.show_correct_if_failed === true,
		[status, test]
	)

	const isChosen = (item: any) => entryMatchesOption(stored, { id: item.id ?? null, choice: item.choice })

	const getMcqColor = (item: any) => {
		if (isChosen(item)) {
			if (stored?.correct) return 'rgba(0, 214, 107, 0.1)'
			else return '#ffd5cc'
		}
		if (showCorrectAnswer || isStudentFailedRightAnswer) {
			if (item?.is_correct) return 'rgba(0, 214, 107, 0.1)'
			else return ''
		}
		return '#FFF'
	}
	const bulletColor = (item: any) => {
		if (isChosen(item)) {
			if (stored?.correct) return 'green'
			else return 'lightRed'
		}
		if (showCorrectAnswer || isStudentFailedRightAnswer) {
			if (item?.is_correct) return 'green'
			else return 'silver'
		}
		return 'silver'
	}
	const bulletState = (item: any) => {
		if (isChosen(item)) return true
		if (showCorrectAnswer || isStudentFailedRightAnswer) {
			if (item?.is_correct) return true
			else return false
		}
		return false
	}

	const liveOptions = (question.options ?? []) as any[]
	// A stored answer whose option was edited away or deleted: still the
	// student's answer, shown from the snapshot text rather than hidden.
	const orphan = stored && !liveOptions.some(item => isChosen(item)) ? stored : null

	return (
		<Spacer p={{ sm: '1rem' }}>
			<QuestionContentWrapper dangerouslySetInnerHTML={{ __html: content }} />
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout px={{ sm: '0.813rem' }} flexDirection="column">
				<AnswersLabel fontSize={{ sm: '0.5rem', lg: '1rem' }}>
					Answers
				</AnswersLabel>
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				<FlexLayout flexDirection={'column'}>
					{liveOptions.map((mcqItem, index) => (
						<FlexLayoutStyle
							alignItems={'center'}
							key={`${mcqItem.id ?? mcqItem.choice}-${index}`}
							mb={{ sm: '0.75rem' }}
							background={getMcqColor(mcqItem)}
						>
							<AnswerBullet filled={bulletState(mcqItem)} state={bulletColor(mcqItem)} />
							<Spacer mx={{ sm: '4px' }} />
							<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<Label dir="auto" htmlFor={mcqItem.choice}>{mcqItem.choice}</Label>
							</Typography>
						</FlexLayoutStyle>
					))}
					{orphan && (
						<FlexLayoutStyle alignItems={'center'} mb={{ sm: '0.75rem' }} background={orphan.correct ? 'rgba(0, 214, 107, 0.1)' : '#ffd5cc'}>
							<AnswerBullet filled state={orphan.correct ? 'green' : 'lightRed'} />
							<Spacer mx={{ sm: '4px' }} />
							<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<Label dir="auto">
									{orphan.answer} — {t('option_no_longer_exists')}
								</Label>
							</Typography>
						</FlexLayoutStyle>
					)}
				</FlexLayout>
			</FlexLayout>
			{question.feedback !== null && (
				<>
					<FeedbackTitle>Feedback</FeedbackTitle>
					<FeedbacktWrapper dangerouslySetInnerHTML={{ __html: stampAutoDir(question.feedback) }} />
				</>
			)}
		</Spacer>
	)
}

export default McqAnswer

const FlexLayoutStyle = styled(FlexLayout)<{ background: string }>`
	background: ${({ background }) => background};
	height: 2.313rem;
	padding: 0.5rem;
	border-radius: 7px;
	input[type='radio']#correct {
		accent-color: #00d66b;
	}
	input[type='radio']#wrong {
		accent-color: #ff0000;
	}
`
const Label = styled.label`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.87);
	font-weight: normal;
	@media screen and (min-width: 769px) {
		font-size: 1.125rem;
	}
`
const AnswerBullet = styled.div<{ filled: boolean; state: string }>`
	border-radius: 50%;
	position: relative;
	background: ${props => (props.filled ? props.theme.colors[props.state] : '')};
	box-shadow: inset 0 0 0 1px #fff;
	outline: 1px solid ${props => (props.filled ? props.theme.colors[props.state] : 'gray')};
	width: 0.535rem;
	height: 0.535rem;
	@media screen and (min-width: 769px) {
		width: 1.25rem;
		height: 1.25rem;
		box-shadow: inset 0 0 0 3px #fff;
	}
`
const AnswersLabel = styled(Typography)`
	color: ${props => props.theme.colors.silver};
	font-size: 1rem;
	font-weight: 300;
	@media screen and (min-width: 769px) {
		margin-bottom: 0.75rem;
	}
`
